import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { FaCookie, FaTimes, FaCheck } from 'react-icons/fa';

import { useApp } from '../../contexts/AppContext';

const slideUp = keyframes`
  from {
    transform: translateY(100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

const slideDown = keyframes`
  from {
    transform: translateY(0);
    opacity: 1;
  }
  to {
    transform: translateY(100%);
    opacity: 0;
  }
`;

const ConsentWrapper = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: ${props => props.theme === 'dark' ? 
    'linear-gradient(145deg, #1e293b 0%, #334155 100%)' : 
    'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)'};
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.15);
  border-top: 3px solid #e63946;
  z-index: 9999;
  animation: ${props => props.isVisible ? slideUp : slideDown} 0.3s ease-in-out;
  display: ${props => props.show ? 'block' : 'none'};
`;

const ConsentContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 1.5rem 2rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 2rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
    gap: 1rem;
    padding: 1.5rem 1rem;
  }
`;

const ConsentText = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  gap: 1rem;
  
  .icon {
    color: #e63946;
    font-size: 1.5rem;
    min-width: 24px;
  }
  
  .text {
    color: ${props => props.theme === 'dark' ? '#e2e8f0' : '#374151'};
    font-size: 0.95rem;
    line-height: 1.5;
    
    a {
      color: #e63946;
      text-decoration: none;
      font-weight: 600;
      
      &:hover {
        text-decoration: underline;
      }
    }
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
    
    .icon {
      font-size: 2rem;
    }
  }
`;

const ConsentActions = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
  
  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
  }
`;

const ConsentButton = styled.button`
  background: ${props => props.variant === 'primary' ? 
    'linear-gradient(45deg, #e63946 0%, #e63946dd 100%)' : 
    'transparent'};
  color: ${props => props.variant === 'primary' ? 
    '#ffffff' : 
    (props.theme === 'dark' ? '#94a3b8' : '#6b7280')};
  border: 1px solid ${props => props.variant === 'primary' ? 
    '#e63946' : 
    (props.theme === 'dark' ? '#475569' : '#d1d5db')};
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  white-space: nowrap;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    background: ${props => props.variant === 'primary' ? 
      'linear-gradient(45deg, #d73847 0%, #d73847dd 100%)' : 
      (props.theme === 'dark' ? '#475569' : '#f3f4f6')};
  }
  
  @media (max-width: 768px) {
    flex: 1;
    justify-content: center;
  }
`;

const CookieConsent = () => {
  const { theme } = useApp();
  const [show, setShow] = useState(false);
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) {
      setShow(true);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem('cookieConsent', 'accepted');
    localStorage.setItem('cookieSettings', JSON.stringify({
      necessary: true,
      functional: true,
      analytics: true,
      marketing: true
    }));
    setIsVisible(false);
    setTimeout(() => setShow(false), 300);
  };

  const handleDecline = () => {
    localStorage.setItem('cookieConsent', 'declined');
    localStorage.setItem('cookieSettings', JSON.stringify({
      necessary: true,
      functional: false,
      analytics: false,
      marketing: false
    }));
    setIsVisible(false);
    setTimeout(() => setShow(false), 300);
  };

  const handleCustomize = () => {
    // Redirigir a la página de cookies para personalizar
    window.location.href = '/cookies';
  };

  if (!show) return null;

  return (
    <ConsentWrapper theme={theme} show={show} isVisible={isVisible}>
      <ConsentContent>
        <ConsentText theme={theme}>
          <FaCookie className="icon" />
          <div className="text">
            Utilizamos cookies para mejorar tu experiencia de navegación, 
            analizar el tráfico del sitio y personalizar el contenido. 
            Al hacer clic en "Aceptar todas", consientes el uso de todas las cookies. 
            Puedes gestionar tus preferencias en nuestra{' '}
            <Link to="/cookies">política de cookies</Link>.
          </div>
        </ConsentText>
        
        <ConsentActions>
          <ConsentButton 
            onClick={handleCustomize}
            theme={theme}
          >
            Personalizar
          </ConsentButton>
          
          <ConsentButton 
            onClick={handleDecline}
            theme={theme}
          >
            <FaTimes />
            Rechazar
          </ConsentButton>
          
          <ConsentButton 
            variant="primary"
            onClick={handleAccept}
            theme={theme}
          >
            <FaCheck />
            Aceptar todas
          </ConsentButton>
        </ConsentActions>
      </ConsentContent>
    </ConsentWrapper>
  );
};

export default CookieConsent; 